import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { MapFilterBloc } from '@connect-services/client/blocs/map-filter.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { AbstractFilterMenuComponent } from '@connect-services/client/components/abstract-filter-menu/abstract-filter-menu.component';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { CustomSearchComponent } from '@connect-services/client/components/custom-search/custom-search.component';
import { EditWarnMessageComponent } from '@connect-services/client/components/edit-warn-message/edit-warn-message.component';
import { FilterMenuSkeletonComponent } from '@connect-services/client/components/filter-menu/filter-menu-content/filter-menu-skeleton.component';
import { MapFilterItemComponent } from '@connect-services/client/components/filter-menu/filter-menu-content/map-filter/map-filter-item/map-filter-item.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { MapFilterService } from '@connect-services/client/services/map-filter.service';
import { MapFilterPermissionsService } from '@connect-services/client/services/map-filter-permissions.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { ProjectSessionStorageService } from '@connect-services/client/services/project-session-storage.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { MapFilter, MapFilterService as SdkMapFilterService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CustomSearchComponent,
        EditWarnMessageComponent,
        FilterMenuSkeletonComponent,
        MapFilterItemComponent,
        RessourcesModule,
    ],
    selector: 'app-map-filter',
    templateUrl: './map-filter.component.html',
})
export class MapFilterComponent extends AbstractFilterMenuComponent implements OnInit {
    protected readonly ButtonType = ButtonType;
    protected readonly CheckboxState = CheckboxState;
    protected readonly checkedFilters: Signal<MapFilter[]> = computed<MapFilter[]>(() => {
        return this.filters().filter((filter: MapFilter) => this.selectedFilters().includes(filter.id));
    });
    protected readonly filters: Signal<MapFilter[]> = computed<MapFilter[]>(() => {
        const filterSearch = this.filterSearch().trim().toLowerCase();

        if (!filterSearch) {
            return this.sortedFilters();
        }

        return this.sortedFilters().filter((filter: MapFilter) =>
            (filter.name ?? '').toLowerCase().includes(filterSearch),
        );
    });
    protected readonly filterSearch: WritableSignal<string> = signal<string>('');
    protected readonly inactiveFilters: Signal<MapFilter[]> = computed<MapFilter[]>(() => {
        return this.filters().filter((filter: MapFilter) => !this.selectedFilters().includes(filter.id));
    });
    protected readonly isEditMode = computed(() => this.projectEditModeService.isEditModeActive());
    protected readonly mapFilterBloc: MapFilterBloc = inject(MapFilterBloc);
    protected readonly mapFilterPermissionsService = inject(MapFilterPermissionsService);
    protected readonly mapFilterService: MapFilterService = inject(MapFilterService);
    protected readonly projectBloc: ProjectBloc = inject(ProjectBloc);
    protected readonly projectSessionStorageService: ProjectSessionStorageService =
        inject(ProjectSessionStorageService);
    protected readonly selectedFilters: WritableSignal<string[]> = signal<string[]>([]);

    private readonly matDialog = inject(MatDialog);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly router: Router = inject(Router);
    private readonly sdkMapFilterService: SdkMapFilterService = inject(SdkMapFilterService);
    private readonly securityService = inject(SecurityService);
    private readonly sortedFilters: Signal<MapFilter[]> = computed<MapFilter[]>(() => {
        return this.mapFilterService.filters().toSorted(this.sortBySelectionThenName.bind(this));
    });
    private readonly translocoService: TranslocoService = inject(TranslocoService);

    public constructor() {
        super();
    }

    public ngOnInit(): void {
        if (!this.mapFilterService.selectedFilters().length) {
            return;
        }

        this.selectedFilters.set(this.mapFilterService.selectedFilters());
    }

    protected closeMenu(): void {
        this.closeFilter.emit();
    }

    protected createFilter(): void {
        if (!this.mapFilterPermissionsService.canManage()) {
            return;
        }

        this.mapFilterBloc.reset();
        this.redirectToFilter();
        this.closeMenu();
    }

    protected deleteFilter(filter: MapFilter): void {
        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('delete'),
                description: this.translocoService.translate('irreversibleAction'),
                title: this.configureDialogTitle(filter),
                type: ModalType.DANGER,
            },
            width: '30vw',
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }
            await firstValueFrom(
                this.sdkMapFilterService.mapFilterControllerDelete({
                    id: filter.id,
                    isShared: filter.isShared,
                }),
            );

            this.mapFilterService.selectedFilters.set(
                this.mapFilterService
                    .selectedFilters()
                    .filter((selectedFilterId: string) => selectedFilterId !== filter.id),
            );

            await this.mapFilterService.initData();
        });
    }

    protected editFilter(filter: MapFilter): void {
        this.redirectToFilter(filter);
        this.closeMenu();
    }

    protected submit(): void {
        this.mapFilterService.selectedFilters.set(this.selectedFilters());
        this.projectSessionStorageService.updateSelectedFilter(
            this.projectBloc.value()!.id,
            this.mapFilterService.selectedFilters(),
        );

        this.closeMenu();
    }

    protected toggleFilterSelection(filter: MapFilter): void {
        this.selectedFilters.update((selected: string[]) => {
            if (selected.includes(filter.id)) {
                return selected.filter((id) => id !== filter.id);
            }

            return [...selected, filter.id];
        });
    }

    private configureDialogTitle(filter: MapFilter): string {
        const userId: string = this.securityService.meSignal()!.id;

        if (filter.isShared && filter.createdBy?.id === userId) {
            return this.translocoService.translate('mapFilter.confirmDeleteOwnSharedFilter');
        }
        if (filter.isShared && filter.createdBy?.id !== userId) {
            return this.translocoService.translate('mapFilter.confirmDeleteSharedFilter', {
                ownerFilterName: filter.createdBy!.fullName,
            });
        }

        return this.translocoService.translate('mapFilter.confirmDeleteFilter');
    }

    private redirectToFilter(filter?: MapFilter): void {
        const route = [RoutePathEnum.PROJECTS, this.projectBloc.value()?.id, RoutePathEnum.LAYER_FILTER];
        if (filter) {
            route.push(filter.id);
        }

        this.router.navigate(route);
    }

    private sortBySelectionThenName(filter1: MapFilter, filter2: MapFilter): number {
        const isFilter1Selected = this.mapFilterService.selectedFilters().includes(filter1.id);
        const isFilter2Selected = this.mapFilterService.selectedFilters().includes(filter2.id);

        if (isFilter1Selected) {
            if (!isFilter2Selected) {
                return -1;
            }

            return filter1.name.localeCompare(filter2.name);
        }

        if (isFilter2Selected) {
            return 1;
        }

        return 0;
    }
}
